import { Store } from '@ngxs/store'
import { DateTime } from 'luxon'
import { DsrActions } from 'src/app/state-man/actions/dsr.actions'
import { FormActions } from 'src/app/state-man/actions/form.actions'
import { JsaActions } from 'src/app/state-man/actions/jsa.actions'
import { JsaFormActions } from 'src/app/state-man/actions/jsa_form.actions'
import { PrfActions } from 'src/app/state-man/actions/prf.actions'
import { Dsr } from 'src/app/state-man/models/dsr.model'
import { Form } from 'src/app/state-man/models/form.model'
import { JsaPhoto } from 'src/app/state-man/models/jsa-photo.model'
import type { JsaStats } from 'src/app/state-man/models/jsa-stats.model'
import { Jsa, JsaForm } from 'src/app/state-man/models/jsa.model'
import { Prf } from 'src/app/state-man/models/prf.model'
import { IsoDate, md, toIsoDate } from './dates'
import { FormCode, formCodeOf } from './form-catalog'
import type { TipKey } from './screens'
import { HIDDEN, LEGACY_STATUS_NAME, PAPER_STATUS, PaperStatus, PaperStatusDef, STATUS_ID_FOR, dsrStatus, formStatus, prfStatus } from './status'

export type PaperKind = 'jsa' | 'dsr' | 'form' | 'prf'

export interface PaperKindDef {
  readonly kind: PaperKind
  readonly slug: 'jsa' | 'dsr' | 'forms' | 'prf'     // URL segment
  readonly title: string                              // list title
  readonly moduleName: string                         // job-card row
  readonly addLabel: string
  readonly calloutTitle: string | null                // null = never a Callout (JSA)
  readonly tip: TipKey                                // tip in job-scoped mode
}

export const PAPER_KINDS = {
  jsa: { kind: 'jsa', slug: 'jsa', title: 'JSAs', moduleName: 'JSA', addLabel: '+ New JSA', calloutTitle: null, tip: 'job-jsa' },
  dsr: { kind: 'dsr', slug: 'dsr', title: 'Daily Service Reports', moduleName: 'Daily Service Report', addLabel: '+ New DSR', calloutTitle: 'Rejected DSRs', tip: 'paper-dsr' },
  form: { kind: 'form', slug: 'forms', title: 'Forms', moduleName: 'Forms', addLabel: '+ New Form', calloutTitle: 'Rejected Forms', tip: 'paper-forms' },
  prf: { kind: 'prf', slug: 'prf', title: 'Parts Request Forms', moduleName: 'Parts Request Form', addLabel: '+ New PRF', calloutTitle: 'Rejected PRFs', tip: 'paper-prf' },
} as const satisfies { readonly [K in PaperKind]: PaperKindDef & { readonly kind: K } }

/** URL segment → kind ('forms' → 'form'); null for anything else. */
export function paperKindOfSlug(slug: string | null | undefined): PaperKind | null {
  const def = Object.values(PAPER_KINDS).find(k => k.slug === slug)
  return def ? def.kind : null
}

/** Job-card row order. Indirect jobs show only 'dsr'. */
export const MODULE_ORDER = ['jsa', 'dsr', 'form', 'prf'] as const satisfies readonly PaperKind[]

interface PaperItemBase {
  readonly uuid: string
  readonly status: PaperStatus
  /** "DSR · 10/23" | "TOF-PRV-87987-2" | "PRF-18607" | "JSA · 10/23" */
  readonly number: string
  readonly woNumber: string
  readonly woId: number | null          // null for JSA rows (keyed by number server-side)
  readonly client: string
  readonly location: string
  readonly worked: IsoDate              // day grouping and job-day bucket key
  readonly submitted: IsoDate | null
  readonly reviewer: string | null      // only once reviewed (Approved, Rejected, Do Not Resubmit)
  readonly reason: string | null        // rejected_reason (Rejected and Do Not Resubmit)
  readonly author: string
  readonly late: boolean                // Dsr.is_late; false elsewhere
  /** Saved on the device, not uploaded yet. */
  readonly pendingUpload: boolean
}

export interface DsrItem extends PaperItemBase { readonly kind: 'dsr'; readonly ref: Dsr; readonly indirect: boolean }
export interface FormItem extends PaperItemBase {
  readonly kind: 'form'
  readonly ref: Form
  readonly code: FormCode | null
  readonly codeLabel: string
  readonly groupUuid: string
  readonly segment: number
  /** The group_number the next Add Another / Duplicate in this chain gets. */
  readonly nextSegment: number
}
export interface PrfItem extends PaperItemBase { readonly kind: 'prf'; readonly ref: Prf; readonly prfType: 'Request' | 'Usage' }
/** JSAs have no review cycle: always 'submitted' (View and a confirmed Delete), never a Callout. */
export interface JsaItem extends Omit<PaperItemBase, 'status'> {
  readonly kind: 'jsa'
  readonly status: 'submitted'
  readonly ref:
    | { readonly as: 'form'; readonly jsa: Jsa; readonly form: JsaForm }
    | { readonly as: 'photos'; readonly jsa: Jsa; readonly photos: readonly JsaPhoto[]; readonly type: 'JSA' | 'Other Photos' }
}
export type PaperItem = DsrItem | FormItem | PrfItem | JsaItem

export type JobDayKey = `${string}|${string}`
export const jobDayKey = (woNumber: string, day: IsoDate): JobDayKey => `${woNumber}|${day}`

/** One job-day's JSA facts: the scheduled rows (needed to create one) plus server and local counts. */
export interface JsaDay {
  readonly jsa: Jsa | null              // type 'JSA' row from jsa/scheduled
  readonly other: Jsa | null            // type 'Other Photos' row
  readonly jsaCount: number             // digital JSA forms + 1 for a paper JSA (however many pages), pending uploads included
  readonly photoCount: number           // other-photos count, same rule
  readonly required: boolean            // a 'JSA' row is scheduled for the day
}

export type StatusCounts = { readonly [K in PaperStatus]: number }
export type KindBuckets = { readonly [K in PaperKind]: readonly PaperItem[] }

export interface PaperBook {
  readonly items: readonly PaperItem[]                                      // newest worked first
  readonly byWo: ReadonlyMap<string, readonly PaperItem[]>
  readonly byJobDay: ReadonlyMap<JobDayKey, KindBuckets>
  readonly jsaDays: ReadonlyMap<JobDayKey, JsaDay>
  readonly rejected: { readonly [K in Exclude<PaperKind, 'jsa'>]: number }
}

export interface PaperSources {
  readonly dsrs: readonly Dsr[]
  readonly forms: readonly Form[]
  readonly prfs: readonly Prf[]
  readonly jsas: readonly Jsa[]
  readonly jsaForms: readonly JsaForm[]     // locally saved digital JSAs live in JsaFormState
  readonly jsaStats: readonly JsaStats[]
}

const reviewed = (s: PaperStatus) => s === 'approved' || s === 'rejected' || s === 'dnr'
const rejectedReason = (s: PaperStatus, reason: string | null | undefined) => (s === 'rejected' || s === 'dnr') && reason ? reason : null

function dsrItem(d: Dsr): DsrItem | null {
  const status = dsrStatus(d)
  const worked = toIsoDate(d.date_worked) ?? toIsoDate(d.created)
  if (status === HIDDEN || !worked) return null
  return {
    kind: 'dsr', ref: d, uuid: d.uuid, status, number: `DSR · ${md(worked)}`,
    woNumber: d.work_order_number, woId: d.work_order_id, client: d.company_name ?? '', location: d.location_name ?? '',
    worked, submitted: status === 'inProgress' ? null : toIsoDate(d.submitted),
    reviewer: reviewed(status) ? d.last_modified_by || null : null, reason: rejectedReason(status, d.rejected_reason),
    author: d.technician ?? '', late: !!d.is_late, pendingUpload: !!d.is_pending_save,
    indirect: d.work_order_id === 1,   // Indirect Time is work order 1
  }
}

function formItem(f: Form, nextSegment: number): FormItem | null {
  const status = formStatus(f)
  const worked = toIsoDate(f.date_worked) ?? toIsoDate(f.created)
  if (status === HIDDEN || !worked) return null
  const code = formCodeOf(f)
  const segment = Number(f.group_number) || 1
  return {
    kind: 'form', ref: f, uuid: f.uuid, status,
    number: f.form_identifier || `${code ?? f.form_name}-${Form.formatDeviceNumber(segment, f.work_order_number)}`,
    woNumber: f.work_order_number, woId: f.work_order_id, client: f.company_name ?? '', location: f.location_name ?? '',
    worked, submitted: status === 'inProgress' ? null : toIsoDate(f.submitted_at),
    reviewer: status === 'approved' ? f.approved_by || null : reviewed(status) ? f.rejected_by || null : null,
    reason: rejectedReason(status, f.rejected_reason), author: f.author ?? '', late: false, pendingUpload: !!f.is_pending_save,
    code, codeLabel: code ?? f.form_name ?? '', groupUuid: f.group_uuid, segment, nextSegment,
  }
}

function prfItem(m: Prf): PrfItem | null {
  const status = prfStatus(m)
  const worked = toIsoDate(m.created_at)
  if (status === HIDDEN || !worked) return null
  return {
    kind: 'prf', ref: m, uuid: m.uuid, status, prfType: Prf.isUsage(m) ? 'Usage' : 'Request',
    // A new PRF has no server id until it uploads. The number is the record id, as before the rename.
    number: m.id > 0 ? `PRF-${m.id}` : `PRF · ${md(worked)}`,
    woNumber: m.work_order_number, woId: m.work_order_id, client: m.company_name ?? '', location: m.location_name ?? '',
    worked, submitted: status === 'inProgress' ? null : toIsoDate(m.updated_at ?? m.created_at),
    reviewer: reviewed(status) ? m.last_modified_by || null : null, reason: rejectedReason(status, m.rejected_reason),
    author: m.requestor ?? '', late: false, pendingUpload: !!m.is_pending_save,
  }
}

function jsaBase(jsa: Jsa, worked: IsoDate) {
  return {
    kind: 'jsa' as const, status: 'submitted' as const, woNumber: jsa.work_order_number, woId: null,
    client: jsa.customer ?? '', location: jsa.location_name ?? '', worked, reviewer: null, reason: null, author: '', late: false,
  }
}

function jsaItems(jsas: readonly Jsa[], jsaForms: readonly JsaForm[]): JsaItem[] {
  const rows = new Map(jsas.map(j => [j.id, j]))
  const out: JsaItem[] = []
  for (const form of jsaForms) {
    const jsa = rows.get(form.jsa_id)
    const worked = jsa && toIsoDate(jsa.due_date)
    if (form.is_pending_delete || !jsa || !worked) continue
    out.push({
      ...jsaBase(jsa, worked), ref: { as: 'form', jsa, form }, uuid: form.uuid, number: `JSA · ${md(worked)}`,
      submitted: toIsoDate(form.modified_at) ?? toIsoDate(form.created_at), pendingUpload: !!form.is_pending_save,
    })
  }
  for (const jsa of jsas) {
    const worked = toIsoDate(jsa.due_date)
    const photos = jsa.photos ?? []
    if (!worked || photos.length === 0) continue
    const type = jsa.type === 'Other Photos' ? 'Other Photos' : 'JSA'
    out.push({
      ...jsaBase(jsa, worked), ref: { as: 'photos', jsa, photos, type }, uuid: `jsa-photos-${jsa.id}`,
      number: `${type === 'JSA' ? 'JSA' : 'Photos'} · ${md(worked)}`,
      submitted: toIsoDate(photos[0]?.created_at) ?? worked, pendingUpload: photos.some(p => p.uploaded === false),
    })
  }
  return out
}

function jsaDays(src: PaperSources): Map<JobDayKey, JsaDay> {
  const stats = new Map<string, number>()
  for (const s of src.jsaStats) {
    const day = toIsoDate(s.due_date)
    if (day) stats.set(`${jobDayKey(s.work_order_number, day)}|${s.jsa_type_id}`, Number(s.count) || 0)
  }
  const localForms = new Map<number, number>()
  for (const f of src.jsaForms) if (!f.is_pending_delete) localForms.set(f.jsa_id, (localForms.get(f.jsa_id) ?? 0) + 1)

  const rows = new Map<JobDayKey, { jsa: Jsa | null; other: Jsa | null }>()
  for (const j of src.jsas) {
    const day = toIsoDate(j.due_date)
    if (!day) continue
    const key = jobDayKey(j.work_order_number, day)
    const entry = rows.get(key) ?? { jsa: null, other: null }
    if (j.type === 'Other Photos') entry.other = j
    else entry.jsa = j
    rows.set(key, entry)
  }

  const days = new Map<JobDayKey, JsaDay>()
  for (const [key, { jsa, other }] of rows) {
    // A paper JSA is one JSA however many pages were photographed.
    const localJsa = jsa ? (localForms.get(jsa.id) ?? 0) + (jsa.photos?.length ? 1 : 0) : 0
    // The server's JSA count adds photo pages and forms together, so it only proves that one exists.
    const serverJsa = (stats.get(`${key}|1`) ?? 0) > 0 ? 1 : 0
    days.set(key, {
      jsa, other,
      jsaCount: Math.max(serverJsa, localJsa),
      photoCount: Math.max(stats.get(`${key}|2`) ?? 0, other?.photos?.length ?? 0),
      required: !!jsa?.is_scheduled,
    })
  }
  return days
}

const emptyBuckets = (): { [K in PaperKind]: PaperItem[] } => ({ jsa: [], dsr: [], form: [], prf: [] })

/** Every DSR, Form, PRF and JSA on the device, normalised once and indexed. Pending deletes are excluded. */
export function buildPaperBook(src: PaperSources): PaperBook {
  const nextSegment = new Map<string, number>()
  for (const f of src.forms) nextSegment.set(f.group_uuid, Math.max(nextSegment.get(f.group_uuid) ?? 1, (Number(f.group_number) || 1) + 1))

  const items: PaperItem[] = [
    ...src.dsrs.filter(d => !d.is_pending_delete).map(dsrItem),
    ...src.forms.filter(f => !f.is_pending_delete).map(f => formItem(f, nextSegment.get(f.group_uuid) ?? 2)),
    ...src.prfs.filter(m => !m.is_pending_delete).map(prfItem),
    ...jsaItems(src.jsas, src.jsaForms),
  ].filter((i): i is PaperItem => i !== null)
  items.sort((a, b) => (a.worked < b.worked ? 1 : a.worked > b.worked ? -1 : 0))

  const byWo = new Map<string, PaperItem[]>()
  const byJobDay = new Map<JobDayKey, { [K in PaperKind]: PaperItem[] }>()
  const rejected = { dsr: 0, form: 0, prf: 0 }
  for (const item of items) {
    const wo = byWo.get(item.woNumber) ?? []
    wo.push(item)
    byWo.set(item.woNumber, wo)
    const key = jobDayKey(item.woNumber, item.worked)
    const buckets = byJobDay.get(key) ?? emptyBuckets()
    buckets[item.kind].push(item)
    byJobDay.set(key, buckets)
    if (item.status === 'rejected') rejected[item.kind]++
  }
  return { items, byWo, byJobDay, jsaDays: jsaDays(src), rejected }
}

export function countByStatus(items: readonly PaperItem[]): StatusCounts {
  const counts = { inProgress: 0, submitted: 0, approved: 0, rejected: 0, dnr: 0 }
  for (const i of items) counts[i.status]++
  return counts
}

export const defOf = (item: PaperItem): PaperStatusDef => PAPER_STATUS[item.status]

/** Delete one item through its slice's upload queue. The only kind → delete-action mapping. Confirming is the caller's job (deletePrompt). */
/** "Submit N Ready" on a form group: every Draft in the chain is queued for upload as Submitted, without opening the editor. */
export function submitDraftForms(store: Store, drafts: readonly FormItem[]): void {
  const forms = drafts.map(d => {
    const form = Form.buildOne(d.ref)
    form.status = LEGACY_STATUS_NAME.submitted
    form.form_status_id = STATUS_ID_FOR.form.submitted
    form.is_pending_save = true
    form.submitted_at = DateTime.now().toFormat('yyyy-MM-dd')
    return new FormActions.UpdateOne(form)
  })
  if (forms.length) store.dispatch([...forms, new FormActions.IncrementPendingCount()])
}

export function deletePaper(store: Store, item: PaperItem): void {
  switch (item.kind) {
    case 'dsr': {
      const dsr = Dsr.buildOne(item.ref)
      dsr.is_pending_delete = true
      store.dispatch([new DsrActions.DeleteOne(dsr), new DsrActions.IncrementPendingCount()])
      return
    }
    case 'form': {
      const form = Form.buildOne(item.ref)
      form.is_pending_delete = true
      store.dispatch([new FormActions.DeleteOne(form), new FormActions.IncrementPendingCount()])
      return
    }
    case 'prf': {
      const prf = Prf.buildOne(JSON.parse(JSON.stringify(item.ref)))
      prf.is_pending_delete = true
      store.dispatch([new PrfActions.DeleteOne(prf), new PrfActions.IncrementPendingCount()])
      return
    }
    case 'jsa': {
      const ref = item.ref
      if (ref.as === 'form') {
        const form = JsaForm.buildOne(ref.form)
        form.is_pending_delete = true
        form.deleted_at = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss')
        // A form that never uploaded only has to leave the device.
        const remove = form.id > 0 ? new JsaFormActions.DeleteOne(form) : new JsaFormActions.ApiDeletedOne(form)
        store.dispatch([remove, new JsaFormActions.IncrementPendingCount()])
        return
      }
      const deletes = ref.photos.map(p => new JsaActions.DeletePhotoFromJsa({ jsa_id: ref.jsa.id, photo: Object.assign(new JsaPhoto(), p) }))
      store.dispatch([...deletes, new JsaActions.IncrementPendingCount()])
      return
    }
  }
}
